import type {
  Booking,
  CreateLinkInput,
  CreateTenantInput,
  DemoLink,
  EmbedSettings,
  FieldProblem,
  LinkInvitation,
  LinkResult,
  Me,
  Prospect,
  SessionGrant,
  Tenant,
  UpdateTenantInput,
} from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: FieldProblem[];
  readonly retryAfterSeconds: number | null;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    details?: FieldProblem[];
    retryAfterSeconds?: number | null;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.details = init.details ?? [];
    this.retryAfterSeconds = init.retryAfterSeconds ?? null;
  }
}

export interface StartSessionInput {
  embedKey: string;
  source: "landing" | "embed";
  parentOrigin?: string | null;
  prospect: Prospect;
}

export interface PublicApi {
  startSession(input: StartSessionInput): Promise<SessionGrant>;
  describeLink(token: string): Promise<LinkInvitation>;
  startLinkSession(token: string, prospect: Prospect): Promise<SessionGrant>;
}

export interface DashboardApi {
  me(): Promise<Me>;
  listLinks(tenantId?: string): Promise<{ links: DemoLink[] }>;
  createLink(input: CreateLinkInput, tenantId?: string): Promise<LinkResult>;
  resendLink(linkId: string, expiresInDays: number, tenantId?: string): Promise<LinkResult>;
  revokeLink(linkId: string, tenantId?: string): Promise<{ link: DemoLink }>;
  getEmbed(tenantId?: string): Promise<EmbedSettings>;
  updateOrigins(origins: string[], tenantId?: string): Promise<EmbedSettings>;
  listBookings(tenantId?: string): Promise<{ bookings: Booking[] }>;
  listTenants(): Promise<{ tenants: Tenant[] }>;
  createTenant(input: CreateTenantInput): Promise<{ tenant: Tenant }>;
  updateTenant(tenantId: string, input: UpdateTenantInput): Promise<{ tenant: Tenant }>;
}

export type ApiClient = PublicApi & DashboardApi;

interface RequestOptions {
  body?: unknown;
  auth?: boolean;
  tenantId?: string | undefined;
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: FieldProblem[] };
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export function createApiClient(
  baseUrl: string,
  getToken: () => string | null | undefined = () => null,
  fetcher: typeof fetch = (...args) => fetch(...args),
): ApiClient {
  const root = baseUrl.replace(/\/+$/, "");

  async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
    const url = new URL(root + path);
    if (options.tenantId) {
      url.searchParams.set("tenant_id", options.tenantId);
    }
    const headers: Record<string, string> = { Accept: "application/json" };
    if (options.body !== undefined) {
      headers["Content-Type"] = "application/json";
    }
    if (options.auth) {
      const token = getToken();
      if (!token) {
        throw new ApiError({ status: 401, code: "unauthenticated", message: "Sign in to continue." });
      }
      headers.Authorization = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await fetcher(url.toString(), {
        method,
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
      });
    } catch {
      throw new ApiError({
        status: 0,
        code: "network_error",
        message: "We couldn't reach TryLook. Check your connection and try again.",
      });
    }

    const text = await response.text();
    const payload = text ? parseJson(text) : null;
    if (!response.ok) {
      const envelope = (payload ?? {}) as ErrorEnvelope;
      const retryAfter = Number(response.headers.get("Retry-After"));
      throw new ApiError({
        status: response.status,
        code: envelope.error?.code ?? "http_error",
        message: envelope.error?.message ?? "Something went wrong. Please try again.",
        details: envelope.error?.details ?? [],
        retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
      });
    }
    return payload as T;
  }

  const segment = encodeURIComponent;

  return {
    startSession: ({ embedKey, source, parentOrigin, prospect }) =>
      request("POST", "/public/demo-sessions", {
        body: {
          embed_key: embedKey,
          source,
          parent_origin: parentOrigin ?? null,
          prospect,
          consent: true,
        },
      }),
    describeLink: (token) => request("GET", `/public/links/${segment(token)}`),
    startLinkSession: (token, prospect) =>
      request("POST", `/public/links/${segment(token)}/sessions`, { body: { prospect, consent: true } }),

    me: () => request("GET", "/me", { auth: true }),
    listLinks: (tenantId) => request("GET", "/links", { auth: true, tenantId }),
    createLink: (input, tenantId) => request("POST", "/links", { auth: true, tenantId, body: input }),
    resendLink: (linkId, expiresInDays, tenantId) =>
      request("POST", `/links/${segment(linkId)}/send`, {
        auth: true,
        tenantId,
        body: { expires_in_days: expiresInDays },
      }),
    revokeLink: (linkId, tenantId) => request("DELETE", `/links/${segment(linkId)}`, { auth: true, tenantId }),
    getEmbed: (tenantId) => request("GET", "/embed", { auth: true, tenantId }),
    updateOrigins: (origins, tenantId) =>
      request("PUT", "/embed/origins", { auth: true, tenantId, body: { allowed_origins: origins } }),
    listBookings: (tenantId) => request("GET", "/bookings", { auth: true, tenantId }),
    listTenants: () => request("GET", "/admin/tenants", { auth: true }),
    createTenant: (input) => request("POST", "/admin/tenants", { auth: true, body: input }),
    updateTenant: (tenantId, input) =>
      request("PATCH", `/admin/tenants/${segment(tenantId)}`, { auth: true, body: input }),
  };
}
